import { z } from 'zod'
import { PACKAGE_NAMES_IN_USE } from '../payments/constants'
import {
  RECURRING_TYPES,
  TAX_PAYER_TYPES,
  TAX_ID_TYPES,
  BRANCH_TYPES,
  REQUIRED_COMPANY_ADDRESS_FIELDS,
} from './constants'

export const makeFreeTrialFormSchema = (t: (key: string) => string) => {
  const baseSchema = z.object({
    recurring: z.enum(RECURRING_TYPES),
    package: z.enum(PACKAGE_NAMES_IN_USE),
    card_name: z.string(),
    code: z.string().optional(),
    taxpayer_type: z.enum(TAX_PAYER_TYPES),
    name: z.string(),
    address: z.string(),
    tax_id: z.string(),
    email: z.string().optional(),
    phone: z.string().optional(),
    tax_id_type: z.enum(TAX_ID_TYPES).default('thai'),
    branch_type: z.enum(BRANCH_TYPES).default('headquarter'),
    branch_name: z.string().optional(),
    sub_district: z.string().optional(),
    district: z.string().optional(),
    province: z.string().optional(),
    zip_code: z.string().optional(),
  })

  type BaseSchemaData = z.infer<typeof baseSchema>

  const validateIndividual = (data: BaseSchemaData, ctx: z.RefinementCtx) => {
    if (!data.tax_id || data.tax_id.length !== 13) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('form.errors.taxIdInvalid'),
        path: ['tax_id'],
      })
    }
  }

  const validateCompany = (data: BaseSchemaData, ctx: z.RefinementCtx) => {
    if (!data.email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('form.errors.emailInvalid'),
        path: ['email'],
      })
    } else if (!z.string().email().safeParse(data.email).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('form.errors.emailInvalid'),
        path: ['email'],
      })
    }

    if (!data.phone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('form.errors.phoneInvalid'),
        path: ['phone'],
      })
    }

    REQUIRED_COMPANY_ADDRESS_FIELDS.forEach((field) => {
      if (!data[field]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.errors.addressRequired'),
          path: [field],
        })
      }
    })

    if (data.branch_type === 'branch' && !data.branch_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('form.errors.branchRequired'),
        path: ['branch_name'],
      })
    }

    if (data.tax_id_type === 'thai') {
      if (!data.tax_id || data.tax_id.length !== 13) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.errors.taxIdInvalid'),
          path: ['tax_id'],
        })
      }
    } else {
      if (!data.tax_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.errors.taxIdRequired'),
          path: ['tax_id'],
        })
      }
    }
  }

  return baseSchema.superRefine((data, ctx) => {
    if (data.taxpayer_type === 'individual') validateIndividual(data, ctx)
    if (data.taxpayer_type === 'company') validateCompany(data, ctx)
  })
}
